import type { TerrierModule } from '../vendor/terrier';

// Helper to convert hex + alpha to ARGB format (0xAARRGGBB)
const hexAlphaToArgb = (hex: string, alpha: number): number => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = Math.round(alpha * 255);
  // eslint-disable-next-line no-bitwise
  return ((a << 24) | (r << 16) | (g << 8) | b) >>> 0;
};

const hexToArgb = (hex: string): number => hexAlphaToArgb(hex, 1);

type ColorEntry = { dBZ: number; color: number; visible: boolean };

const createColorMapFromConfig = (
  Terrier: TerrierModule,
  config: ColorEntry[],
): unknown =>
  Terrier.createColorMap(
    config.map((c) => c.dBZ),
    config.map((c) => c.color),
    config.map((c) => c.visible),
  );

// Hex color values for radar legend
export const RADAR_LEGEND_COLORS = {
  LIGHT_BLUE: '#13A1EC',
  BLUE: '#0C50E4',
  BRIGHT_GREEN: '#1BE220',
  GREEN: '#0CBE0B',
  DARK_GREEN: '#0C8608',
  YELLOW: '#E9E90F',
  GOLD: '#DAB30F',
  ORANGE: '#EA9312',
  RED: '#F10D0F',
  DARK_RED: '#CB0F0C',
  MAROON: '#B20B09',
  MAGENTA: '#F153C2',
  PURPLE: '#CD4DFF',
  WHITE: '#FCFBFA',
} as const;

// ARGB colors (0xAARRGGBB format) - Watch Duty radar theme
const TRANSPARENT = 0x00000000;
const LIGHT_BLUE_20 = hexAlphaToArgb(RADAR_LEGEND_COLORS.LIGHT_BLUE, 0.2);
const LIGHT_BLUE_40 = hexAlphaToArgb(RADAR_LEGEND_COLORS.LIGHT_BLUE, 0.4);
const LIGHT_BLUE_80 = hexAlphaToArgb(RADAR_LEGEND_COLORS.LIGHT_BLUE, 0.8);
const BLUE = hexToArgb(RADAR_LEGEND_COLORS.BLUE);
const BRIGHT_GREEN = hexToArgb(RADAR_LEGEND_COLORS.BRIGHT_GREEN);
const GREEN = hexToArgb(RADAR_LEGEND_COLORS.GREEN);
const DARK_GREEN = hexToArgb(RADAR_LEGEND_COLORS.DARK_GREEN);
const YELLOW = hexToArgb(RADAR_LEGEND_COLORS.YELLOW);
const GOLD = hexToArgb(RADAR_LEGEND_COLORS.GOLD);
const ORANGE = hexToArgb(RADAR_LEGEND_COLORS.ORANGE);
const RED = hexToArgb(RADAR_LEGEND_COLORS.RED);
const DARK_RED = hexToArgb(RADAR_LEGEND_COLORS.DARK_RED);
const MAROON = hexToArgb(RADAR_LEGEND_COLORS.MAROON);
const MAGENTA = hexToArgb(RADAR_LEGEND_COLORS.MAGENTA);
const PURPLE = hexToArgb(RADAR_LEGEND_COLORS.PURPLE);
const WHITE = hexToArgb(RADAR_LEGEND_COLORS.WHITE);

/**
 * Radar color map configuration
 * - dBZ: Reflectivity threshold in decibels relative to Z
 * - color: ARGB format for Terrier
 * - visible: Whether to render this threshold
 */
const RADAR_CONFIG: ColorEntry[] = [
  // No precipitation / noise (-30 to 0 dBZ)
  { dBZ: -30, color: TRANSPARENT, visible: false },
  { dBZ: -25, color: TRANSPARENT, visible: false },
  { dBZ: -20, color: TRANSPARENT, visible: false },
  { dBZ: -15, color: TRANSPARENT, visible: false },
  { dBZ: -10, color: TRANSPARENT, visible: false },
  { dBZ: -5, color: TRANSPARENT, visible: false },
  // Light rain/drizzle (0-20 dBZ)
  { dBZ: 0, color: LIGHT_BLUE_20, visible: true },
  { dBZ: 5, color: LIGHT_BLUE_40, visible: true },
  { dBZ: 10, color: LIGHT_BLUE_80, visible: true },
  { dBZ: 15, color: BLUE, visible: true },
  // Light to moderate rain (20-35 dBZ)
  { dBZ: 20, color: BRIGHT_GREEN, visible: true },
  { dBZ: 25, color: GREEN, visible: true },
  { dBZ: 30, color: DARK_GREEN, visible: true },
  // Moderate to heavy rain (35-50 dBZ)
  { dBZ: 35, color: YELLOW, visible: true },
  { dBZ: 40, color: GOLD, visible: true },
  { dBZ: 45, color: ORANGE, visible: true },
  // Heavy rain, possible hail (50-65 dBZ)
  { dBZ: 50, color: RED, visible: true },
  { dBZ: 55, color: DARK_RED, visible: true },
  { dBZ: 60, color: MAROON, visible: true },
  // Extreme, large hail likely (65+ dBZ)
  { dBZ: 65, color: MAGENTA, visible: true },
  { dBZ: 70, color: PURPLE, visible: true },
  { dBZ: 75, color: WHITE, visible: true },
];

/** Create radar color map using Watch Duty colors */
export const createRadarColorMap = (Terrier: TerrierModule): unknown =>
  createColorMapFromConfig(Terrier, RADAR_CONFIG);
